import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";
import { Button } from "../ui/button";
import { BrandLockup } from "./Logo";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "../ui/dropdown-menu";
import { LogOut, User as UserIcon, CreditCard, LayoutDashboard, Crown, Sparkles, Eye, Users } from "lucide-react";

const PlanBadge = ({ plan }) => {
  const map = {
    OWNER: "border-warn/40 text-warn bg-warn/[0.06]",
    PRO: "border-bull text-bull bg-bull/[0.08]",
    TRADER: "border-bull/50 text-bull",
    FREE: "border-white/15 text-neutral-400",
  };
  return (
    <span className={`font-mono text-[9px] tracking-[0.18em] px-1.5 py-0.5 border ${map[plan] || map.FREE}`} data-testid={`plan-badge-${plan}`}>
      {plan}
    </span>
  );
};

const LangToggle = () => {
  const { lang, setLang } = useI18n();
  const isLoggedIn = !!useAuth()?.user;
  return (
    <div className="hidden sm:flex items-center border border-white/10 h-9 overflow-hidden" data-testid="lang-toggle">
      <button
        className={`h-full px-2.5 font-mono text-xs transition-colors ${lang === "en" ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
        onClick={() => setLang("en", { persist: isLoggedIn })}
        data-testid="lang-en-btn"
        aria-label="English"
      >EN</button>
      <button
        className={`h-full px-2.5 font-mono text-xs transition-colors border-l border-white/10 ${lang === "fr" ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
        onClick={() => setLang("fr", { persist: isLoggedIn })}
        data-testid="lang-fr-btn"
        aria-label="Français"
      >FR</button>
    </div>
  );
};

export const Header = () => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const onLanding = location.pathname === "/";

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050505]/85 backdrop-blur-md" data-testid="app-header">
      <div className="mx-auto max-w-7xl px-6 md:px-8 h-14 flex items-center justify-between">
        <Link to="/" className="group" data-testid="logo-link">
          <BrandLockup />
        </Link>
        <nav className="flex items-center gap-2">
          <Link to="/community" className="hidden sm:inline-flex items-center px-3 h-9 font-mono text-xs tracking-wider text-neutral-300 hover:text-bull transition-colors" data-testid="nav-community-link">
            {t("nav_community")}
          </Link>
          <Link to="/pricing" className="hidden sm:inline-flex items-center px-3 h-9 font-mono text-xs tracking-wider text-neutral-300 hover:text-bull transition-colors" data-testid="nav-pricing-link">
            {t("nav_pricing")}
          </Link>
          <LangToggle />
          {user ? (
            <>
              {onLanding && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-neutral-300 hover:text-white hover:bg-white/5"
                  onClick={() => navigate("/dashboard")}
                  data-testid="nav-dashboard-btn"
                >
                  {t("nav_dashboard")}
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 px-3 h-9 border border-white/10 bg-white/[0.02] hover:bg-white/5 transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    {user.role === "OWNER" ? (
                      <Crown className="h-3.5 w-3.5 text-warn" strokeWidth={2} />
                    ) : (
                      <span className="h-1.5 w-1.5 rounded-full bg-bull animate-pulse-glow" />
                    )}
                    <span className="font-mono text-[11px] text-neutral-400 truncate max-w-[120px]">{user.email}</span>
                    <PlanBadge plan={user.role === "OWNER" ? "OWNER" : (user.plan || "FREE")} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-[#0a0a0a] border-white/10 rounded-none min-w-[200px] font-mono text-xs"
                >
                  <DropdownMenuLabel className="text-neutral-500 uppercase tracking-[0.18em] text-[10px]">
                    {user.role === "OWNER" ? "OWNER · UNLIMITED" : `${user.plan || "FREE"} PLAN`}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-white/5 rounded-none">
                    <LayoutDashboard className="h-3.5 w-3.5 mr-2 text-bull" strokeWidth={1.5} />
                    {t("nav_dashboard")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/watchlist")} className="cursor-pointer focus:bg-white/5 rounded-none" data-testid="menu-watchlist">
                    <Eye className="h-3.5 w-3.5 mr-2 text-bull" strokeWidth={1.5} />
                    {t("nav_watchlist")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/community")} className="cursor-pointer focus:bg-white/5 rounded-none" data-testid="menu-community">
                    <Users className="h-3.5 w-3.5 mr-2 text-bull" strokeWidth={1.5} />
                    {t("nav_community")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/billing")} className="cursor-pointer focus:bg-white/5 rounded-none" data-testid="menu-billing">
                    <CreditCard className="h-3.5 w-3.5 mr-2 text-bull" strokeWidth={1.5} />
                    {t("nav_billing")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/pricing")} className="cursor-pointer focus:bg-white/5 rounded-none" data-testid="menu-pricing">
                    <Sparkles className="h-3.5 w-3.5 mr-2 text-bull" strokeWidth={1.5} />
                    {t("nav_pricing")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={() => logout()}
                    className="cursor-pointer focus:bg-bear/10 text-bear rounded-none"
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} />
                    {t("nav_logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="text-neutral-300 hover:text-white hover:bg-white/5"
                onClick={() => navigate("/login")}
                data-testid="nav-login-btn"
              >
                {t("nav_login")}
              </Button>
              <Button
                size="sm"
                className="bg-white text-black hover:bg-neutral-200 rounded-none px-4"
                onClick={() => navigate("/login?mode=signup")}
                data-testid="nav-signup-btn"
              >
                {t("nav_signup")} →
              </Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};
